import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CartItem } from '../../types/cart';
import { getCSRFToken } from '../../utils/csrf';
import { dispatchCartCount } from '../../utils/cartBadge';
import { route } from '../../utils/route';

const STORAGE_KEY = 'cart_packs';

interface PackTrackingEntry {
  addedAt: string;
  title: string;
  itemKeys: string[];
}

interface PackTracking {
  [packId: string]: PackTrackingEntry;
}

interface PacksInCartProps {
  cartItems: CartItem[];
  onPackRemoved: () => void;
}

export function PacksInCart({ cartItems, onPackRemoved }: PacksInCartProps) {
  const [packTracking, setPackTracking] = useState<PackTracking>({});
  const [removingPackId, setRemovingPackId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setPackTracking(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Error loading pack tracking:', e);
    }
  }, []);

  const cartItemKeys = useMemo(() => {
    return new Set(cartItems.map(item => `${item.id}:${item.format_id}`));
  }, [cartItems]);

  // Clean up packs that have no items left in cart
  useEffect(() => {
    const packIds = Object.keys(packTracking);
    if (packIds.length === 0) return;

    const orphaned = packIds.filter(
      id => !packTracking[id].itemKeys.some(key => cartItemKeys.has(key))
    );
    if (orphaned.length === 0) return;

    const next = { ...packTracking };
    orphaned.forEach(id => delete next[id]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setPackTracking(next);
  }, [cartItemKeys]);

  const packsInCart = useMemo(() => {
    return Object.entries(packTracking)
      .filter(([_, entry]) => entry.itemKeys.some(key => cartItemKeys.has(key)))
      .map(([packId, entry]) => ({
        id: packId,
        title: entry.title,
        itemKeys: entry.itemKeys,
        itemsInCart: entry.itemKeys.filter(key => cartItemKeys.has(key)).length,
        totalItems: entry.itemKeys.length,
      }));
  }, [packTracking, cartItemKeys]);

  const removePack = useCallback(async (packId: string, itemKeys: string[]) => {
    setRemovingPackId(packId);

    try {
      const items = itemKeys
        .filter(key => cartItemKeys.has(key))
        .map(key => {
          const [id, format_id] = key.split(':').map(Number);
          return { id, format_id, quantity: 0 };
        });

      if (items.length > 0) {
        const res = await fetch(route('api.cart.add_item'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': getCSRFToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'include',
          body: JSON.stringify({ items }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.summary?.count != null) {
            dispatchCartCount(data.summary.count);
          }
        }
      }

      const newTracking = { ...packTracking };
      delete newTracking[packId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newTracking));
      setPackTracking(newTracking);

      onPackRemoved();
    } catch (e) {
      console.error('Error removing pack:', e);
    } finally {
      setRemovingPackId(null);
    }
  }, [cartItemKeys, packTracking, onPackRemoved]);

  if (packsInCart.length === 0) return null;

  return (
    <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <p className="text-gray-500 text-sm m-0 mb-3">
        Algunos de tus productos fueron agregados por los siguientes packs:
      </p>
      <ul className="list-none m-0 p-0">
        {packsInCart.map(pack => (
          <li key={pack.id} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md mb-2 last:mb-0 border border-gray-200">
            <div className="flex-1 min-w-0">
              <span className="text-gray-800 text-sm font-medium">{pack.title}</span>
              {pack.itemsInCart < pack.totalItems && (
                <span className="text-gray-400 text-xs ml-2">
                  ({pack.itemsInCart} de {pack.totalItems} productos)
                </span>
              )}
            </div>
            <button
              className="bg-white text-red-500 border border-red-500 py-1.5 px-3 rounded text-[13px] cursor-pointer transition-all duration-200 min-w-[70px] flex items-center justify-center hover:enabled:bg-red-500 hover:enabled:text-white disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={() => removePack(pack.id, pack.itemKeys)}
              disabled={removingPackId === pack.id}
              type="button"
            >
              {removingPackId === pack.id ? (
                <span className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                'Quitar'
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
